import { useEffect, useState } from "react";
import { useI18n } from "@/i18n";
import { loungeCopy } from "@/lib/loungeCopy";
import api from "@/lib/api";
import { LoungeShell, LoungeSectionTitle } from "@/components/lounge/LoungeShell";
import { LoungeHero, BrandIntro, Gallery } from "@/components/lounge/LoungeSections";
import { LoungeForm } from "@/components/lounge/LoungeForm";
import { SampleSelection } from "@/components/lounge/SampleSelection";
import { LoungeMediaLibrary } from "@/components/lounge/LoungeMediaLibrary";
import { resolveLoungeUrl } from "@/lib/loungeUrl";
import { useAutoTranslate, AutoTranslatedBadge } from "@/lib/loungeTranslate";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { PressGateProvider } from "@/components/lounge/PressAccreditationGate";
import { trackLoungeAccessOnce } from "@/lib/loungeTrack";

function useRef_() {
  const [ref] = useState(() => {
    try {
      const r = new URLSearchParams(window.location.search).get("ref") || "";
      if (r) sessionStorage.setItem("lounge_ref", r);
      return r || sessionStorage.getItem("lounge_ref") || "";
    } catch {
      return "";
    }
  });
  return ref;
}

export default function AmbassadorLounge() {
  const { lang } = useI18n();
  return (
    <PressGateProvider lang={lang} mode="ambassador">
      <AmbassadorLoungeInner />
    </PressGateProvider>
  );
}

function AmbassadorLoungeInner() {
  const { lang } = useI18n();
  const c = loungeCopy(lang);
  const ref = useRef_();
  const [data, setData] = useState({ hero: "", grid: [], ambassadors: [], languages: [], intro: {}, guidelines: {}, media: {} });

  useEffect(() => {
    document.title = "Ambassador Lounge · Brema 1969";
    api.get("/public/lounge/ambassador").then(({ data }) => setData(data)).catch(() => {});
    trackLoungeAccessOnce("ambassador", lang);
  }, [lang]);

  // Linee guida Kit: usa il testo compilato per la lingua corrente; se l'admin ha
  // scritto solo in IT, traduci automaticamente (fallback); altrimenti usa i default localizzati.
  const glDedicated = (data.guidelines || {})[lang] || [];
  const glItSource = (data.guidelines || {})["it"] || [];
  const glNeedTr = lang !== "it" && glDedicated.length === 0 && glItSource.length > 0;
  const { translated: glTr, translating: glTranslating } = useAutoTranslate(glNeedTr ? glItSource : [], lang);
  const kitGuidelines = glDedicated.length > 0 ? glDedicated : (glNeedTr ? glTr : c.kit_guidelines);

  return (
    <LoungeShell active="ambassador" languages={data.languages}>
      <LoungeHero image={data.hero} eyebrow={c.amb_eyebrow} title={(data.hero_title || {})[lang] || c.amb_title} subtitle={(data.hero_sub || {})[lang] || c.amb_sub}>
        <a href="#candidatura">
          <Button className="rounded-full bg-primary px-6 font-semibold text-primary-foreground hover:bg-primary/90" data-testid="lounge-hero-apply-btn">
            {c.cta_apply} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </a>
        <a href="#kit">
          <Button variant="outline" className="rounded-full border-border bg-card px-6 text-foreground hover:bg-brema-hover" data-testid="lounge-hero-kit-btn">
            {c.cta_kit}
          </Button>
        </a>
      </LoungeHero>

      <BrandIntro intro={data.intro} />
      <Gallery images={data.grid} title={c.presentation_images_title} />

      {/* Griglia ambassador */}
      <section className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-24" data-testid="lounge-ambassadors">
        <LoungeSectionTitle eyebrow="Team" title={c.ambassadors_title} subtitle={c.ambassadors_sub} testId="lounge-ambassadors-title" />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {data.ambassadors.map((a, i) => (
            <div key={i} className="group text-center" data-testid={`lounge-ambassador-card-${i}`}>
              <div className="aspect-[3/4] w-full overflow-hidden rounded-xl border border-border bg-muted">
                {resolveLoungeUrl(a.photo) ? (
                  <img src={resolveLoungeUrl(a.photo)} alt={a.name} loading="lazy" className="h-full w-full object-cover grayscale transition-all duration-500 group-hover:grayscale-0" />
                ) : null}
              </div>
              <div className="mt-3 text-sm font-semibold text-foreground">{a.name}</div>
              <div className="text-xs font-medium uppercase tracking-wide text-brand">{a.discipline}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Libreria multimediale */}
      <LoungeMediaLibrary media={data.media} />

      {/* Candidatura ambassador */}
      <section id="candidatura" className="border-t border-border bg-card/40 px-5 py-16 sm:px-8 sm:py-24" data-testid="lounge-apply">
        <div className="mx-auto max-w-2xl">
          <LoungeForm
            copy={c}
            testidPrefix="lounge-apply"
            endpoint="/public/lounge/ambassador-apply"
            refValue={ref}
            title={c.apply_title}
            subtitle={c.apply_sub}
            fields={[
              { name: "name", labelKey: "f_name", required: true },
              { name: "email", labelKey: "f_email", type: "email", required: true },
              { name: "social", labelKey: "f_social" },
              { name: "discipline", labelKey: "f_discipline" },
              { name: "message", labelKey: "f_message", type: "textarea" },
            ]}
          />
        </div>
      </section>

      {/* Linee guida Brema per gli Ambassador */}
      <section id="kit" className="mx-auto max-w-3xl px-5 py-16 sm:px-8 sm:py-24" data-testid="lounge-kit">
        <div data-testid="lounge-kit-guidelines">
          <LoungeSectionTitle eyebrow="Kit" title={c.kit_guidelines_title} testId="lounge-kit-title" />
          {glNeedTr && (
            <div className="mb-4">
              <AutoTranslatedBadge translating={glTranslating} labels={c} testId="lounge-kit-translated-badge" />
            </div>
          )}
          <ul className="space-y-4">
            {kitGuidelines.map((g, i) => (
              <li key={i} className="flex gap-3 text-[15px] leading-relaxed text-muted-foreground">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
                <span>{g}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <SampleSelection sourcePage="ambassador" refValue={ref} />
    </LoungeShell>
  );
}
